import React from 'react';
import { Link } from 'react-router-dom';
import LandingNav from './LandingNav';
import Footer from './Footer';

const modelProFeatures = [
  {
    title: 'Multiple Wallets',
    description:
      'Create separate wallets for long-term holdings, active trading, and Web3 activity so your assets stay organized and isolated by purpose.',
    bullets: [
      'Create and manage up to 5 segregated wallets',
      'Reduce risk by separating strategy-specific balances',
      'Clear portfolio organization for personal or team use'
    ],
    image: '/images/all/multi-wallet-connection.png'
  },
  {
    title: 'Legacy Transfer',
    description:
      'Set up a continuity plan that transfers access to a trusted recipient after prolonged inactivity, without exposing your active wallet controls today.',
    bullets: [
      'Inheritance-style transfer workflow',
      'Inactivity-based trigger logic',
      'Built to support long-term self-custody planning'
    ],
    image: '/images/all/backup-system-illustration.png'
  },
  {
    title: 'Theft Guard',
    description:
      'Add biometric MFA for withdrawals and sensitive approvals. Even if a phone is compromised, high-value transfers require an additional security step.',
    bullets: [
      'Biometric confirmation with custom threshold',
      'Protect outgoing transfers against unauthorized access',
      'Can be enabled for all transactions'
    ],
    image: '/images/all/biometric-authentication.png'
  },
  {
    title: 'Advanced Web3 Firewall',
    description:
      'Get risk-aware transaction insights before signing. Sensitive approvals are classified into clear risk levels to reduce blind-signing exposure.',
    bullets: [
      'Real-time risk levels for Web3 approvals',
      'Alerts for unusual permissions and known scam patterns',
      'Extra confirmation for high-risk interactions'
    ],
    image: '/images/all/transaction-monitor.png'
  },
  {
    title: 'Private Transaction Mode',
    description:
      'Improve transaction privacy by rotating receiving addresses for supported assets while keeping balances and history accessible in one wallet view.',
    bullets: [
      'Address rotation for stronger privacy',
      'Balances stay unified across generated addresses',
      'Designed for repeat-use wallets on public chains'
    ],
    image: '/images/all/encryption-process-diagram.png'
  }
];

const mpcPageSections = [
  {
    title: 'Not Your Keys, Not Your Crypto: Outdated',
    text:
      'The new standard in self-custody is not about manually protecting one seed phrase forever. Modern wallet security distributes trust across independent components so access is harder to compromise and easier to recover safely.',
    bullets: [
      'Seed phrases create a single high-value target',
      'Human error is a common source of wallet loss',
      'Distributed cryptography reduces single-point failure'
    ]
  },
  {
    title: 'The false dichotomy: centralized exchanges vs non-custodial wallets',
    text:
      'Users should not have to choose between convenience and security. MPC-based self-custody enables a third path: user-controlled wallets with institutional-grade signing architecture and practical recovery.',
    bullets: [
      'Self-custodial by design',
      'Improved recovery without seed phrase dependence',
      'Better usability for everyday Web3 interactions'
    ]
  },
  {
    title: 'What is MPC and how does it work?',
    text:
      'MPC (Multi-Party Computation) replaces one private key with secret shares generated in separate locations. Transactions are signed through secure collaboration between shares, without reconstructing full key material in one place.',
    bullets: [
      'Two-share or threshold-share cryptographic architecture',
      'Secret shares stay isolated during wallet operations',
      'A valid blockchain signature is produced collaboratively'
    ]
  },
  {
    title: 'Why MPC is a better user experience than a “seed phrase” wallet',
    text:
      'MPC security can be wrapped in a simple mobile flow. Users keep control of assets while avoiding fragile paper backups and high-stress recovery steps associated with legacy wallet models.',
    bullets: [
      'No 12/24-word phrase to store manually',
      'Smoother onboarding and recovery journey',
      'Lower risk from phishing and phrase exposure'
    ]
  },
  {
    title: 'MPC cryptography is gaining adoption',
    text:
      'MPC has become a proven standard in institutional custody and is now powering consumer wallets that need both strong security and reliable user experience at scale.',
    bullets: [
      'Battle-tested cryptographic approach',
      'Suitable for high-value transaction environments',
      'Increasingly adopted across modern wallet infrastructure'
    ]
  }
];

const recoveryGuideSections = [
  {
    heading: 'What is a Recovery Kit?',
    paragraphs: [
      'Your Recovery Kit is the security framework that lets you restore wallet access when you switch devices, reinstall the app, or recover from phone loss.',
      'Instead of relying on one seed phrase, recovery uses independent factors under your control. This lowers the chance of account loss caused by phrase exposure, phishing, or backup mistakes.'
    ],
    bullets: [
      'Email authentication for account ownership verification',
      'Recovery file stored in your personal cloud account',
      'Optional biometric verification for enhanced account security'
    ]
  },
  {
    heading: 'What is Recovery 3FA?',
    paragraphs: [
      '3FA means wallet restoration can require three independent factors: something you control, something you store, and something you are.',
      'This layered approach is designed so one compromised item is not enough to fully recover and take over your wallet.'
    ],
    bullets: [
      'Factor 1: Verified email access',
      'Factor 2: Recovery file in cloud backup',
      'Factor 3: Biometric check (when enabled)'
    ]
  },
  {
    heading: 'How do I recover my wallet?',
    paragraphs: [
      'If you change phones or reinstall the app, recovery is straightforward as long as your factors are available.',
      'Follow the process below in order to restore access securely.'
    ],
    bullets: [
      'Install the wallet app on your new device',
      'Sign in with the same email used during setup',
      'Confirm the verification link from your inbox',
      'Allow access to your synced recovery file',
      'Complete biometric verification if configured',
      'Finish setup and verify wallet balances'
    ]
  },
  {
    heading: 'Troubleshooting during recovery',
    paragraphs: [
      'Recovery issues are usually caused by cloud sync delays, permissions, or device security settings. Use the checks below before retrying.',
      'If recovery still fails, support can walk you through factor validation and secure restoration options.'
    ],
    bullets: [
      'Confirm your cloud drive has fully synced on the new device',
      'Check that the recovery file is present and not modified',
      'Ensure camera permission is enabled for biometric checks',
      'Enable device lock/passcode and retry authentication',
      'Verify adequate cloud storage and network connectivity'
    ]
  }
];

const proFeatureShowcase = [
  {
    title: 'Multiple Wallets',
    subtitle: 'Create up to 5 segregated wallets',
    description:
      'Organize digital assets by strategy, risk level, or use case. Segregated wallets improve operational hygiene and make asset management clearer.',
    points: [
      'Separate long-term holdings from active trading',
      'Reduce exposure between independent wallet buckets',
      'Cleaner reporting and balance tracking'
    ],
    image: '/images/all/multi-wallet-connection.png'
  },
  {
    title: 'Legacy Transfer',
    subtitle: 'Pass on your crypto legacy',
    description:
      'Define a trusted recipient flow that helps preserve asset continuity in case of prolonged inactivity, with strong safeguards built into activation.',
    points: [
      'Inheritance-style digital asset continuity',
      'Rule-based activation after inactivity window',
      'Built for long-term self-custody planning'
    ],
    image: '/images/all/backup-system-illustration.png'
  },
  {
    title: 'Theft Guard',
    subtitle: 'Approve transactions with biometrics',
    description:
      'Protect withdrawals and sensitive approvals with biometric MFA and configurable thresholds so high-risk actions require stronger verification.',
    points: [
      'Set custom value thresholds for MFA triggers',
      'Add stronger control for outgoing transfers',
      'Optionally apply protection to every transaction'
    ],
    image: '/images/all/biometric-authentication.png'
  },
  {
    title: 'Advanced Web3 Firewall',
    subtitle: 'Alerts against Web3 attacks',
    description:
      'Get risk-aware transaction insights before signing. Potentially dangerous permissions and unusual approval patterns are surfaced clearly in-context.',
    points: [
      'Risk-level classification for approvals',
      'Warnings for suspicious contract interactions',
      'Extra confirmation on high-sensitivity actions'
    ],
    image: '/images/all/transaction-monitor.png'
  },
  {
    title: 'Private Transaction Mode',
    subtitle: 'Improve wallet privacy posture',
    description:
      'Enhance on-chain privacy for supported assets by rotating receive addresses while preserving unified portfolio visibility in one account experience.',
    points: [
      'Address rotation for repeat transactions',
      'Balances remain visible in one wallet view',
      'Helpful for operational privacy use cases'
    ],
    image: '/images/all/encryption-process-diagram.png'
  },
  {
    title: 'Priority 24/7 support',
    subtitle: 'Get fast expert help when it matters',
    description:
      'Receive accelerated security support for urgent account and transaction concerns, so you can respond quickly during high-stress incidents.',
    points: [
      '24/7 access to support assistance',
      'Faster response path for premium users',
      'Incident-focused guidance for recovery and protection'
    ],
    image: '/images/all/ai-threat-detection-panel.png'
  }
];

const proFaqs = [
  {
    q: 'What makes Pro different from standard wallet security?',
    a: 'Pro adds advanced controls like Theft Guard thresholds, enhanced Web3 risk alerts, multiple wallet segregation, and priority security support.'
  },
  {
    q: 'Can I apply biometric checks to every transaction?',
    a: 'Yes. You can configure security behavior so additional verification is required for all outgoing actions or only above selected thresholds.'
  },
  {
    q: 'Is the Web3 Firewall blocking my transactions by default?',
    a: 'The firewall focuses on clear risk visibility and warnings. You receive context before signing so decisions are intentional and informed.'
  },
  {
    q: 'Who should use Pro Security Features?',
    a: 'Pro is ideal for users managing larger balances, active dApp permissions, and workflows that require stricter operational controls.'
  }
];

const pageContent = {
  model: {
    badge: 'Security Model',
    title: 'Self-custody more secure than a hardware wallet',
    intro:
      'Upgrade to a self-custody model with no seed phrase vulnerability, powered by MPC and layered account protection.',
    highlights: [
      'No single point of failure from a seed phrase',
      'Two independently created secret shares',
      '3-factor recovery with user-controlled factors',
      'Transaction integrity checks before broadcast'
    ],
    sections: [
      {
        heading: 'No seed phrase vulnerability',
        text:
          'Traditional wallets rely on a 12/24-word seed phrase as a single high-risk secret. AureumVault replaces this pattern with MPC architecture so wallet security does not depend on one recoverable string that can be phished, leaked, or misplaced.'
      },
      {
        heading: 'How the model works',
        text:
          'At wallet creation, two secret shares are generated independently: one stays on your device and one is secured remotely. During signing, these shares compute together to produce a valid blockchain signature without revealing full key material in a single location.'
      }
    ],
    faqs: [
      {
        q: 'Can AureumVault access user funds?',
        a: 'No. All transactions are initiated by the user. The architecture is self-custodial and designed so the platform cannot unilaterally spend assets.'
      },
      {
        q: 'Is this approach audited?',
        a: 'The security model follows open, peer-reviewed MPC practices and is intended to support regular third-party security review.'
      },
      {
        q: 'Why is this safer than typical wallets?',
        a: 'It removes the single-secret failure mode and adds layered checks for recovery and transaction approvals.'
      }
    ]
  },
  mpc: {
    badge: 'MPC Technology',
    title: 'MPC: Say goodbye to seed phrase vulnerabilities',
    intro:
      'MPC replaces private-key centralization with distributed signing, enabling seedless self-custody with stronger security and smoother recovery.',
    highlights: [
      'Institutional-grade cryptography adapted for consumers',
      'Secret shares never exposed to each other',
      'Signing requires secure share collaboration',
      'No full private key present in one place'
    ],
    sections: [
      {
        heading: 'What is an MPC wallet?',
        text:
          'In an MPC wallet, secret shares are generated in separate environments and used together for signing. This removes the classic single point of failure in private-key and seed phrase wallet designs.'
      },
      {
        heading: 'Not Your Keys, Not Your Crypto: outdated',
        text:
          'The modern self-custody approach is no longer about writing down one master secret. With MPC, users keep control while avoiding the weakest legacy pattern: a single recoverable phrase.'
      },
      {
        heading: 'Why MPC is a better user experience',
        text:
          'MPC allows secure recovery and device migration flows without forcing users to handle fragile backup phrases manually. This reduces user error and improves real-world safety.'
      },
      {
        heading: 'MPC cryptography is gaining adoption',
        text:
          'MPC has been used by institutions protecting large-value assets for years. Consumer wallets now bring the same security principles to everyday users through intuitive mobile experiences.'
      }
    ],
    faqs: [
      {
        q: 'Is MPC proven technology?',
        a: 'Yes. MPC is a mature cryptographic field used in institutional custody and threshold-signature systems.'
      },
      {
        q: 'Does MPC remove all risk?',
        a: 'No security system is risk-free, but MPC significantly reduces key exposure risk by removing single-secret dependencies.'
      },
      {
        q: 'Can a compromised phone alone drain assets?',
        a: 'The model is specifically designed so one compromised component is insufficient for full wallet takeover.'
      }
    ]
  },
  recovery: {
    badge: '3FA Recovery',
    title: 'Secure wallet recovery with 3-factor authentication',
    intro:
      'Recovery is designed to be simple and secure: restore wallet access using independent authentication factors instead of relying on a single seed phrase.',
    highlights: [
      'Email authentication for account ownership',
      'Recovery file stored in your personal cloud',
      'Optional biometric 3D FaceLock for extra assurance',
      'Fast recovery flow for new-device onboarding'
    ],
    sections: [
      {
        heading: 'What is a recovery kit?',
        text:
          'The recovery kit is the mechanism that allows secure wallet restoration after app reinstall or device switch. It combines independent factors so one leaked item is not enough for unauthorized recovery.'
      },
      {
        heading: 'Recovery factors',
        text:
          'Core factors include verified email and a recovery file in your cloud storage. Biometric verification can be enabled as an additional security factor for sensitive account operations.'
      },
      {
        heading: 'How to recover your wallet',
        text:
          'Install the app on the new device, sign in with the same email, verify the login link, and complete the configured checks. Once validated, wallet access is restored without seed phrase entry.'
      },
      {
        heading: 'Troubleshooting highlights',
        text:
          'If recovery file sync is delayed, confirm cloud sync completion and check your drive directly. Ensure camera permissions, device lock settings, and local/cloud storage availability are correctly configured.'
      }
    ],
    faqs: [
      {
        q: 'Can the recovery file alone unlock my wallet?',
        a: 'No. The file is not a private key and cannot restore access by itself.'
      },
      {
        q: 'Should I edit or move the recovery file?',
        a: 'No. Keep it intact in your cloud backup path so restoration works reliably.'
      },
      {
        q: 'What if I switch from iOS to Android or vice versa?',
        a: 'Cross-device recovery is supported as long as you can validate the same account factors and recovery setup.'
      }
    ]
  },
  pro: {
    badge: 'Pro Security Features',
    title: 'Advanced Web3 protection with Pro Security Features',
    intro:
      'AureumVault Pro includes enhanced self-custody controls for users who need stronger transaction security, privacy, and support.',
    highlights: [
      'Multiple Wallets for asset segregation',
      'Legacy Transfer for continuity planning',
      'Theft Guard with biometric MFA approvals',
      'Advanced Web3 Firewall and risk alerts'
    ],
    sections: [
      {
        heading: 'Multiple Wallets',
        text:
          'Create segregated wallets for different strategies, assets, or counterparties. Separation helps improve operational hygiene and reduces cross-wallet exposure.'
      },
      {
        heading: 'Legacy Transfer',
        text:
          'Set up continuity rules to pass assets to a trusted recipient after a defined inactivity period, supporting long-term digital inheritance planning.'
      },
      {
        heading: 'Theft Guard',
        text:
          'Apply biometric MFA to withdrawals and selected approvals. Configure a threshold so transactions above your chosen amount require extra verification.'
      },
      {
        heading: 'Advanced Web3 Firewall',
        text:
          'The firewall classifies risky approvals and smart contract interactions into clear risk levels, helping users identify suspicious permissions, wallet drainers, and known scam patterns before signing.'
      },
      {
        heading: 'Private Transaction Mode',
        text:
          'Improve on-chain privacy by rotating receive addresses for supported assets while keeping balances and history unified in the main wallet experience.'
      },
      {
        heading: 'Priority 24/7 support',
        text:
          'Get accelerated support response during critical moments so suspicious activity and urgent account issues can be handled quickly.'
      }
    ],
    faqs: [
      {
        q: 'Can I set MFA on every transaction?',
        a: 'Yes. Pro controls can be tuned so every outgoing action requires additional biometric confirmation.'
      },
      {
        q: 'Does Pro include Web3 transaction insights?',
        a: 'Yes. Transaction context and risk warnings are surfaced before final approval to reduce blind signing risk.'
      },
      {
        q: 'Who is Pro designed for?',
        a: 'It is ideal for users and teams managing larger balances, frequent dApp approvals, and stricter operational security policies.'
      }
    ]
  }
};

const SecurityModelPage = () => (
  <div className="min-h-screen bg-deepspace overflow-x-hidden">
    <LandingNav />

    <section className="relative pt-28 pb-14 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[#071019] via-[#0b1724] to-[#10252e]" />
      <div className="relative max-w-6xl mx-auto">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary max-w-2xl mb-8">
          Self-custody more secure than a hardware wallet
        </h1>
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div className="space-y-4">
            <p className="text-text-secondary leading-relaxed">
              Upgrade to a self-custody model with no seed phrase vulnerability, powered by MPC and layered account protection.
            </p>
            <div className="flex flex-wrap gap-3">
              <span className="px-3 py-1 rounded-full bg-cyber-blue/20 text-cyber-blue text-sm">No seed phrase vulnerability</span>
              <span className="px-3 py-1 rounded-full bg-cyber-gold/20 text-cyber-gold text-sm">MPC secured</span>
              <span className="px-3 py-1 rounded-full bg-cyber-green/20 text-cyber-green text-sm">Self-custodial</span>
            </div>
          </div>
          <div className="relative">
            <div className="absolute inset-0 bg-cyber-gold/20 blur-3xl rounded-3xl" />
            <div className="relative bg-dark-card/70 border border-dark-border/60 rounded-3xl p-8">
              <img src="/images/all/insurance-protection-symbol.png" alt="Security lock" className="w-28 h-28 mx-auto object-contain" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#0a1522]">
      <div className="max-w-6xl mx-auto text-center">
        <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
          MPC: Say goodbye to seed phrase vulnerabilities
        </h2>
        <p className="text-text-secondary max-w-3xl mx-auto leading-relaxed mb-10">
          Instead of one fragile backup phrase, two independently created secret shares collaborate to sign transactions securely without exposing full key material in one place.
        </p>
        <div className="grid md:grid-cols-3 gap-6 text-left">
          <div className="bg-dark-card/60 border border-dark-border/60 rounded-2xl p-6">
            <h3 className="text-cyber-blue font-semibold mb-2">No single point of failure</h3>
            <p className="text-text-secondary text-sm">Security does not depend on one recoverable phrase that can be phished, leaked, or lost.</p>
          </div>
          <div className="bg-dark-card/60 border border-dark-border/60 rounded-2xl p-6">
            <h3 className="text-cyber-green font-semibold mb-2">Distributed signing model</h3>
            <p className="text-text-secondary text-sm">Device and remote shares compute together for transaction authorization.</p>
          </div>
          <div className="bg-dark-card/60 border border-dark-border/60 rounded-2xl p-6">
            <h3 className="text-cyber-gold font-semibold mb-2">Institutional-grade cryptography</h3>
            <p className="text-text-secondary text-sm">MPC principles used in high-value custody are adapted for everyday wallet users.</p>
          </div>
        </div>
      </div>
    </section>

    <section className="py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto text-center">
        <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">Secure wallet recovery</h2>
        <p className="text-text-secondary leading-relaxed max-w-3xl mx-auto mb-10">
          Recover access safely using independent account factors instead of relying on a single seed phrase. The recovery flow is designed for real-world device loss while preserving strict control.
        </p>
        <div className="grid md:grid-cols-3 gap-6 text-left">
          <div className="bg-dark-card/60 border border-dark-border/60 rounded-2xl p-6">
            <h3 className="text-text-primary font-semibold mb-2">1. Email authentication</h3>
            <p className="text-text-secondary text-sm">Verify account ownership using the same email linked to wallet creation.</p>
          </div>
          <div className="bg-dark-card/60 border border-dark-border/60 rounded-2xl p-6">
            <h3 className="text-text-primary font-semibold mb-2">2. Recovery file</h3>
            <p className="text-text-secondary text-sm">Use the recovery file stored in your personal cloud backup location.</p>
          </div>
          <div className="bg-dark-card/60 border border-dark-border/60 rounded-2xl p-6">
            <h3 className="text-text-primary font-semibold mb-2">3. Biometric factor</h3>
            <p className="text-text-secondary text-sm">Add biometric verification as an additional guard for sensitive actions.</p>
          </div>
        </div>
      </div>
    </section>

    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#06393a]">
      <div className="max-w-6xl mx-auto space-y-14">
        {modelProFeatures.map((feature, index) => (
          <div key={feature.title} className="grid lg:grid-cols-2 gap-8 items-center">
            <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
              <div className="bg-[#0e2430] border border-cyan-500/20 rounded-3xl p-6">
                <img src={feature.image} alt={feature.title} className="w-full max-h-72 object-contain" />
              </div>
            </div>
            <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
              <h3 className="text-2xl lg:text-3xl font-bold text-text-primary mb-3">{feature.title}</h3>
              <p className="text-text-secondary leading-relaxed mb-4">{feature.description}</p>
              <div className="space-y-2">
                {feature.bullets.map((bullet, bulletIndex) => (
                  <div key={bulletIndex} className="flex items-start gap-3">
                    <span className="w-2 h-2 mt-2 rounded-full bg-cyber-gold" />
                    <span className="text-text-primary text-sm">{bullet}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>

    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#0a1522]">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="text-2xl lg:text-3xl font-bold text-text-primary mb-3">Need help right now?</h2>
        <p className="text-text-secondary mb-6">Security support is available 24/7 for urgent account and wallet protection issues.</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/auth" className="px-8 py-3 bg-gradient-to-r from-cyber-blue to-cyber-gold text-white rounded-xl font-semibold">
            Connect Wallet
          </Link>
          <Link to="/" className="px-8 py-3 border border-cyber-blue/50 text-cyber-blue rounded-xl font-semibold">
            Back to Home
          </Link>
        </div>
      </div>
    </section>

    <Footer />
  </div>
);

const MpcTechnologyPage = () => (
  <div className="min-h-screen bg-deepspace overflow-x-hidden">
    <LandingNav />

    <section className="relative pt-28 pb-14 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[#050a12] via-[#0a1320] to-[#0f1f2b]" />
      <div className="relative max-w-6xl mx-auto">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary max-w-3xl mb-6">
          What is an MPC-wallet?
        </h1>
        <p className="text-text-secondary leading-relaxed max-w-3xl mb-8">
          MPC wallets remove seed phrase dependence by distributing signing authority across independent secret shares. You stay in control, without relying on one fragile recovery secret.
        </p>
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div className="flex flex-wrap gap-3">
            <span className="px-3 py-1 rounded-full bg-cyber-blue/20 text-cyber-blue text-sm">Seedless architecture</span>
            <span className="px-3 py-1 rounded-full bg-cyber-gold/20 text-cyber-gold text-sm">MPC secured</span>
            <span className="px-3 py-1 rounded-full bg-cyber-green/20 text-cyber-green text-sm">User-controlled custody</span>
          </div>
          <div className="relative">
            <div className="absolute inset-0 bg-cyber-gold/20 blur-3xl rounded-3xl" />
            <div className="relative bg-dark-card/70 border border-dark-border/60 rounded-3xl p-8">
              <img src="/images/all/ai-protection-visual.png" alt="MPC wallet security" className="w-full max-h-56 object-contain" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#0a1522]">
      <div className="max-w-6xl mx-auto space-y-12">
        {mpcPageSections.map((section, index) => (
          <div key={section.title} className="bg-dark-card/55 border border-dark-border/60 rounded-3xl p-7 lg:p-10">
            <h2 className="text-2xl lg:text-3xl font-bold text-text-primary mb-4">{section.title}</h2>
            <p className="text-text-secondary leading-relaxed mb-5">{section.text}</p>
            <div className="space-y-2">
              {section.bullets.map((bullet, bulletIndex) => (
                <div key={bulletIndex} className="flex items-start gap-3">
                  <span className={`w-2 h-2 mt-2 rounded-full ${index % 2 === 0 ? 'bg-cyber-gold' : 'bg-cyber-blue'}`} />
                  <span className="text-text-primary text-sm">{bullet}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>

    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#061f2a]">
      <div className="max-w-5xl mx-auto text-center">
        <h2 className="text-2xl lg:text-3xl font-bold text-text-primary mb-3">Build with stronger wallet foundations</h2>
        <p className="text-text-secondary mb-6">
          Move beyond seed phrase limitations with MPC-based self-custody, designed for modern security and real-world usability.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/auth" className="px-8 py-3 bg-gradient-to-r from-cyber-blue to-cyber-gold text-white rounded-xl font-semibold">
            Connect Wallet
          </Link>
          <Link to="/security/model" className="px-8 py-3 border border-cyber-blue/50 text-cyber-blue rounded-xl font-semibold">
            View Security Model
          </Link>
        </div>
      </div>
    </section>

    <Footer />
  </div>
);

const ThreeFaRecoveryPage = () => (
  <div className="min-h-screen bg-[#f5f7fb] overflow-x-hidden">
    <LandingNav />

    <section className="pt-28 pb-10 px-4 sm:px-6 lg:px-8 bg-white border-b border-slate-200">
      <div className="max-w-4xl mx-auto">
        <div className="inline-flex items-center px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-sm font-medium mb-4">
          Security / 3FA Recovery
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">What is AureumVault&apos;s Recovery Kit?</h1>
        <p className="text-slate-600 leading-relaxed max-w-3xl">
          A practical, layered account recovery system built to replace fragile seed phrase workflows with safer, user-controlled recovery factors.
        </p>
      </div>
    </section>

    <section className="py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 lg:p-10 shadow-sm">
        <div className="space-y-10">
          {recoveryGuideSections.map((section) => (
            <article key={section.heading} className="border-b border-slate-200 pb-8 last:border-b-0 last:pb-0">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">{section.heading}</h2>
              <div className="space-y-3 mb-4">
                {section.paragraphs.map((paragraph, idx) => (
                  <p key={idx} className="text-slate-700 leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>
              <ul className="space-y-2">
                {section.bullets.map((bullet, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-slate-700">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-cyber-blue" />
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </div>
    </section>

    <section className="pb-14 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-sm text-center">
        <h3 className="text-2xl font-semibold text-slate-900 mb-3">Need help with account recovery?</h3>
        <p className="text-slate-600 mb-6">Our support team can assist with secure recovery troubleshooting and next-step guidance.</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/auth" className="px-8 py-3 bg-gradient-to-r from-cyber-blue to-cyber-gold text-white rounded-xl font-semibold">
            Open Wallet
          </Link>
          <Link to="/security/model" className="px-8 py-3 border border-cyber-blue/50 text-cyber-blue rounded-xl font-semibold">
            Back to Security Model
          </Link>
        </div>
      </div>
    </section>

    <Footer />
  </div>
);

const ProSecurityFeaturesPage = () => (
  <div className="min-h-screen bg-[#07151f] overflow-x-hidden">
    <LandingNav />

    <section className="relative pt-28 pb-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[#07151f] via-[#102532] to-[#18323d]" />
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-20 left-[10%] w-72 h-72 bg-cyber-blue/20 blur-3xl rounded-full" />
        <div className="absolute bottom-10 right-[10%] w-72 h-72 bg-cyber-gold/20 blur-3xl rounded-full" />
      </div>
      <div className="relative max-w-6xl mx-auto text-center">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary mb-4">
          AureumVault Pro Security Features
        </h1>
        <p className="text-text-secondary max-w-3xl mx-auto leading-relaxed mb-8">
          Advanced self-custody protection for users who want stronger controls, clearer Web3 risk signals, and faster support response in critical moments.
        </p>
        <img
          src="/images/all/insurance-protection-symbol.png"
          alt="Pro security shield"
          className="w-20 h-20 object-contain mx-auto opacity-90"
        />
      </div>
    </section>

    <section className="py-12 px-4 sm:px-6 lg:px-8 bg-[#173746]">
      <div className="max-w-6xl mx-auto space-y-14">
        {proFeatureShowcase.map((feature, index) => (
          <div key={feature.title} className="grid lg:grid-cols-2 gap-8 items-center">
            <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
              <div className="bg-[#0c2230] border border-cyan-400/20 rounded-3xl p-6 shadow-[0_20px_50px_rgba(0,0,0,0.35)]">
                <img src={feature.image} alt={feature.title} className="w-full h-auto max-h-80 object-contain" />
              </div>
            </div>
            <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
              <h2 className="text-2xl lg:text-3xl font-bold text-text-primary mb-1">{feature.title}</h2>
              <p className="text-cyber-gold text-sm font-semibold mb-3">{feature.subtitle}</p>
              <p className="text-text-secondary leading-relaxed mb-4">{feature.description}</p>
              <div className="space-y-2">
                {feature.points.map((point, pointIndex) => (
                  <div key={pointIndex} className="flex items-start gap-3">
                    <span className="w-2 h-2 mt-2 rounded-full bg-cyber-blue" />
                    <span className="text-text-primary text-sm">{point}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>

    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#07151f]">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-2xl lg:text-3xl font-bold text-text-primary text-center mb-8">Frequently asked questions</h2>
        <div className="space-y-4">
          {proFaqs.map((item, index) => (
            <div key={index} className="bg-dark-card/60 border border-dark-border/60 rounded-2xl p-6">
              <h3 className="text-text-primary font-semibold mb-2">{item.q}</h3>
              <p className="text-text-secondary text-sm leading-relaxed">{item.a}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link to="/auth" className="inline-flex px-8 py-3 bg-gradient-to-r from-cyber-blue to-cyber-gold text-white rounded-xl font-semibold">
            Upgrade Your Security
          </Link>
        </div>
      </div>
    </section>

    <Footer />
  </div>
);

const SecurityInfoPage = ({ config }) => (
  <div className="min-h-screen bg-deepspace overflow-x-hidden">
    <LandingNav />

    <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-deepspace via-deepspace-100 to-deepspace-200" />
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyber-blue/10 rounded-full blur-3xl animate-pulse-slow" />
        <div
          className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-cyber-gold/10 rounded-full blur-3xl animate-pulse-slow"
          style={{ animationDelay: '2s' }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto">
        <div className="inline-flex items-center px-4 py-2 rounded-full border border-cyber-gold/40 text-cyber-gold text-sm font-semibold mb-6">
          {config.badge}
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-text-primary mb-6 max-w-4xl">
          {config.title}
        </h1>
        <p className="text-lg sm:text-xl text-text-secondary leading-relaxed max-w-3xl">
          {config.intro}
        </p>
      </div>
    </section>

    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-deepspace-400">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {config.highlights.map((item, index) => (
            <div
              key={index}
              className="bg-dark-card/60 backdrop-blur-sm border border-dark-border/50 rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.5)]"
            >
              <div className="w-10 h-10 rounded-lg bg-cyber-blue/15 text-cyber-blue font-bold flex items-center justify-center mb-4">
                {index + 1}
              </div>
              <p className="text-text-primary leading-relaxed">{item}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto space-y-10">
        {config.sections.map((section, index) => (
          <div key={index} className="bg-dark-card/50 backdrop-blur-sm border border-dark-border/50 rounded-3xl p-8 lg:p-10">
            <h2 className="text-2xl lg:text-3xl font-bold text-text-primary mb-4">{section.heading}</h2>
            <p className="text-text-secondary leading-relaxed text-base sm:text-lg">{section.text}</p>
          </div>
        ))}

        <div className="bg-dark-card/50 backdrop-blur-sm border border-dark-border/50 rounded-3xl p-8 lg:p-10">
          <h2 className="text-2xl lg:text-3xl font-bold text-text-primary mb-6">Frequently asked questions</h2>
          <div className="space-y-6">
            {config.faqs.map((item, index) => (
              <div key={index} className="border-b border-dark-border/60 pb-5 last:border-b-0">
                <h3 className="text-lg font-semibold text-text-primary mb-2">{item.q}</h3>
                <p className="text-text-secondary leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gradient-to-r from-cyber-blue/10 via-cyber-gold/10 to-cyber-green/10 border border-cyber-blue/30 rounded-3xl p-8 text-center">
          <h3 className="text-2xl font-bold text-text-primary mb-3">Ready to secure your wallet?</h3>
          <p className="text-text-secondary mb-6">Use AureumVault to get advanced Web3 protection with a modern self-custody experience.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/auth" className="px-8 py-3 bg-gradient-to-r from-cyber-blue to-cyber-gold text-white font-semibold rounded-xl hover:scale-105 transition-all duration-300">
              Connect Wallet
            </Link>
            <Link to="/" className="px-8 py-3 border border-cyber-blue/50 text-cyber-blue font-semibold rounded-xl hover:bg-cyber-blue/10 transition-all duration-300">
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </section>

    <Footer />
  </div>
);

export { SecurityModelPage };
export { MpcTechnologyPage };
export { ThreeFaRecoveryPage };
export { ProSecurityFeaturesPage };

